import { useEffect, useRef, useState } from 'react';

const TYPE_MS = 35;
const HOLD_MS = 6000;
const BETWEEN_MS = 350;

type Mode = 'typing' | 'holding' | 'between';

export const useTypingPlaceholder = (
	phrases: string[],
	enabled: boolean,
): string => {
	const idxRef = useRef(
		phrases.length > 0 ? Math.floor(Math.random() * phrases.length) : 0,
	);
	const charIdxRef = useRef(1);
	const modeRef = useRef<Mode>('typing');
	const [text, setText] = useState(() =>
		enabled && phrases.length > 0 ? (phrases[idxRef.current]?.[0] ?? '') : '',
	);

	useEffect(() => {
		if (!enabled || phrases.length === 0) {
			setText('');
			return;
		}

		const reducedMotion =
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		let cancelled = false;

		const schedule = (ms: number, fn: () => void) => {
			timeoutId = setTimeout(() => {
				if (!cancelled) fn();
			}, ms);
		};

		const tick = () => {
			if (cancelled) return;
			const phrase = phrases[idxRef.current] ?? '';

			if (reducedMotion) {
				setText(phrase);
				schedule(HOLD_MS * 4, () => {
					idxRef.current = (idxRef.current + 1) % phrases.length;
					tick();
				});
				return;
			}

			const mode = modeRef.current;

			if (mode === 'typing') {
				charIdxRef.current += 1;
				setText(phrase.slice(0, charIdxRef.current));
				if (charIdxRef.current >= phrase.length) {
					modeRef.current = 'holding';
					schedule(HOLD_MS, tick);
				} else {
					schedule(TYPE_MS, tick);
				}
			} else if (mode === 'holding') {
				charIdxRef.current = 0;
				setText('');
				modeRef.current = 'between';
				schedule(BETWEEN_MS, tick);
			} else {
				idxRef.current = (idxRef.current + 1) % phrases.length;
				modeRef.current = 'typing';
				tick();
			}
		};

		tick();

		return () => {
			cancelled = true;
			if (timeoutId !== null) clearTimeout(timeoutId);
		};
	}, [enabled, phrases]);

	return text;
};
